<template>
  <div class="search-bar-wrapper listing-detail-header" :class="{ 'has-top-app-bar': showOpenAppBar, 'app-entry-disabled': isCps }">
    <div class="search-bar-content" :style="barStyle">
      <div class="search-bar">
        <div class="back" @click="$router.back()"><van-icon name="arrow-left" color="#666" /></div>
        <div class="search-input" @click="$router.push('/search-input?first_page=productDetail')">
          <van-icon name="search" color="#999" />
          <span class="search-text">Search Products</span>  
        </div>
        <div class="quick-btn" @click="handleWishListClick">
          <van-icon name="like-o" v-show="!isWish" />
          <van-icon name="like" v-show="isWish" color="#DD3131" />
        </div>
        <div v-if="!isCps" class="quick-btn" @click="handleShareClick"><van-icon name="share-o" /></div>
        <van-popover
          v-model:show="showPopover"
          :actions="actions"
          theme="dark"
          placement="bottom-end"
          @select="handleMoreItemClick"
        >
          <template #reference>
            <div class="quick-btn"><van-icon name="ellipsis" /></div>
          </template>
        </van-popover>
      </div>
      <div class="mark-bar">
        <div v-for="(item, idx) in marks" :key="idx" class="mark" @click="handleMarkClick(item)">
          <span class="mark-label" :select="selectMarkPoint === item.point ? 1 : 0">{{ item.label }}</span>
        </div>
      </div>
    </div>
    <KSShareStoreSharePopup v-model:show="sharePopupShow" @share:click="handleShareItemClick">
      <div class="share-title">Share</div>
      <div class="share-product-card">
        <img v-lazy="productImage" alt="" class="product-image" />

        <p class="product-name">{{ productName }}</p>
      </div>
    </KSShareStoreSharePopup>
  </div>

  <ClientOnly v-if="!isCps">
    <div class="bottom-app-bar" v-show="showOpenAppBar">
      <img class="logo" src="/imgs/icon_48.png" alt="">
      <div class="tips">
        <p class="title">Kilimall - Affordable & Safe Shopping</p>
        <p class="title-desc">Open App to find more deals & extra savings</p>
      </div>
      <a
        :href="bottomBannerDeepLinkUrl"
        class="open-btn"
        @click="handleOpenClick"
      >Open</a>
    </div>

    <div class="top-app-bar" v-show="showOpenAppBar">
      <img class="logo" src="/imgs/icon_48.png" alt="">
      <div class="tips">
        <p class="title">Kilimall - Affordable & Safe Shopping</p>
        <p class="title-desc">Open APP to find more deals</p>
      </div>
      <a
        :href="topBannerDeepLinkUrl"
        class="open-btn"
        @click="handleOpenClick"
      >Open</a>
    </div>

    <a
      v-show="showSideAppFloat"
      :href="sideFloatDeepLinkUrl"
      class="side-app-float"
      @click="handleOpenClick"
    >
      <img class="logo" src="/imgs/icon_48.png" alt="">
      <span class="text">Open in Kilimall APP</span>
      <van-icon name="arrow" class="arrow" />
    </a>
  </ClientOnly>
</template>

<script setup lang="ts">
import { wishListing, delWishListing } from '@/api/models/product'
import { share } from '@/components/KSShareStore/share'
import type { ITrackConfig } from '../mixins'
import { getTrafficSourceParts } from '@/utils/traffic-source'


interface IMark { label: string, point: string }
interface IAction { text: string, icon: string, path: string }
const props = defineProps<{
  isCps?: boolean
  source: 1 | 0,
  id: number,
  listingId: number,
  isWish: boolean,
  productImage: string,
  productName: string,
  trackBaseData?: ITrackConfig
}>()
const emits = defineEmits<{
  (e: 'update:show-bar', v: boolean): void
  (e: 'add:wish', v: boolean): void
}>()
const { $throttle, $fcWidgetOpen, $SENSORS_EVENT } = useNuxtApp()

const marks = computed<IMark[]>(() => [
  { label: 'Product', point: 'product' },
  { label: 'Reviews', point: 'reviews' },
  { label: 'Description', point: 'description' },
  ...(!props.isCps ? [{ label: 'Recommend', point: 'recommend' }] : [])
])
const selectMarkPoint = ref<string>('product')
const barStyle = reactive<{ opacity: number, display: string }>({ opacity: 0, display: 'none' })

const handleMarkClick = (item: IMark) => {
  selectMarkPoint.value = item.point
  const el = document.getElementById(item.point)

  if(el) {
    window.scrollTo({ top: el!.offsetTop - 92, behavior: 'smooth' })
  }
}

const handleShareItemClick = (e: any) => {
  share(
    e,
    { 
      href: `${useCurrentHost()}/listing/${props.listingId}?source=productDetails-share`,
      title: '🌟 Check out this amazing product! 😍 Click the link and see why everyone’s raving about it! 🚀🛒',
      shareContents: props.productName
    },
    'shareProduct'
  )
}

const initMarkPos = (scrollTop: number) => {
  marks.value.forEach((item) => {
    const el = document.getElementById(item.point)

    if(el && scrollTop > el.offsetTop - 150) {
      selectMarkPoint.value = item.point
    }
  })
}

const handleScroll = $throttle(() => {
  const scrollTop = document.documentElement.scrollTop
  barStyle.display = scrollTop > 0 ? 'block' : 'none'
  barStyle.opacity = scrollTop >= 140 ? 1 : scrollTop / 140

  initMarkPos(scrollTop)
}, 15)

const appEntryEligible = ref<boolean>(false)
const showOpenAppBar = computed(() => !props.isCps && appEntryEligible.value)
// 右侧浮窗：无关闭按钮，常驻显示（不受底部 bottom-app-bar 关闭影响）
const showSideAppFloat = computed(() => !props.isCps && appEntryEligible.value)
watch(showOpenAppBar, show => emits('update:show-bar', show), { immediate: true })
const initAppOpenBar = () => {
  // 仅在客户端执行：SSR 默认不显示，避免浮窗闪一下再隐藏
  if(!process.client) return

  const win = window as Window & { androidWebObject?: unknown; flutter_inappwebview?: unknown }
  const isInKilimallApp = !!(win.androidWebObject || win.flutter_inappwebview)
  const isTiktokApp = isTiktok().value || /TikTok/i.test(navigator.userAgent)
  // iOS / Android / Facebook / WhatsApp 均展示；Kilimall App 内、TikTok 内置浏览器不展示
  const shouldShow = !isInKilimallApp && !isTiktokApp

  document.documentElement.setAttribute('data-kili-app-entry', shouldShow ? 'browser' : 'native')
  appEntryEligible.value = shouldShow
}
const { getDeepLinkUrl, handleOpenAppClick: handleOpenClick } = useProductDeepLink(
  () => props.id,
  () => props.source,
  () => ({
    source: props.trackBaseData?.source,
    trafficSourceMeta: props.trackBaseData?.trafficSourceMeta,
  }),
)
const topBannerDeepLinkUrl = getDeepLinkUrl('top-banner')
const bottomBannerDeepLinkUrl = getDeepLinkUrl('bottom-banner')
const sideFloatDeepLinkUrl = getDeepLinkUrl('side-float')

const handleWishListClick = () => {
  const route = useRoute()
  const { isAd } = route.query || {}
  const source = props.trackBaseData?.source || (route.query.source as string) || ''
  const [
    internalLatestTrafficSourceModule = '',
    internalLatestTrafficSourceType = '',
    insideLatestTrafficSourceValue = '',
    internalFirstTrafficSourcePage = '',
    internalLatestTrafficSourcePage = ''
  ] = getTrafficSourceParts(source, props.trackBaseData?.trafficSourceMeta)

  const isAdVal = !!props.trackBaseData?.isAd || isAd === 'ad'
  const isNewTagVal = !!props.trackBaseData?.isNewTag
  const isChoiceTagVal = !!props.trackBaseData?.isChoiceTag
  const requestIdVal = props.trackBaseData?.requestId || ''

  const action = props.isWish
    ? delWishListing(
        props.listingId!,
        internalLatestTrafficSourceModule,
        internalLatestTrafficSourceType,
        insideLatestTrafficSourceValue,
        isAdVal,
        internalFirstTrafficSourcePage,
        internalLatestTrafficSourcePage
      )
    : wishListing(
        props.listingId!,
        internalLatestTrafficSourceModule,
        internalLatestTrafficSourceType,
        insideLatestTrafficSourceValue,
        isAdVal,
        isNewTagVal,
        requestIdVal,
        internalFirstTrafficSourcePage,
        internalLatestTrafficSourcePage,
        isChoiceTagVal
      )

  action.then(() => {
    emits('add:wish', !props.isWish)
  })
}
const sharePopupShow = ref(false)
const handleShareClick = () => {
  sharePopupShow.value = true
}
const showPopover = ref<boolean>(false)
const actions: IAction[] = [
  { text: 'Account', icon: 'contact-o', path: '/user-center' },
  { text: 'Wish list', icon: 'like-o', path: '/wish-list' },
  { text: 'Home', icon: 'wap-home-o', path: '/' },
  { text: 'Message', icon: 'chat-o', path: '/message' },
  { text: 'FAQ', icon: 'question-o', path: 'FAQ' }
]

const handleFcWidgetOpenClick = async() => {
  showLoadingToast({ duration: 0 })
  try {
    await $fcWidgetOpen()

    closeToast()
  } catch(e) {
    showToast('fresh-chat loading error')
  }
}
const handleMoreItemClick = async(e: IAction) => {
  if(/^http(s)?:\/\//.test(e.path)) {
     window.open(e.path)
  } else if(e.path === 'FAQ') {
    // freshchat sdk
    await handleFcWidgetOpenClick()
  } else {
    useRouter().push(e.path)
  }
}
onMounted(() => {
  initAppOpenBar()
  handleScroll()

  window.addEventListener('scroll', handleScroll)
})
</script>

<style lang="scss" scoped>
// top-app-bar 高度：padding 16*2 + logo 90，SCSS 中 px 会统一转 rem
$top-app-bar-offset: 122px;

.side-app-float {
  position: fixed;
  top: 300px;
  right: 0;
  z-index: 98;
  display: flex;
  align-items: center;
  padding: 10px 16px 10px 10px;
  background: #fff;
  border-radius: 60px 0 0 60px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  text-decoration: none;
  .logo {
    width: 56px;
    height: 56px;
    min-width: 56px;
    border-radius: 50%;
    margin-right: 10px;
  }
  .text {
    font-size: 24px;
    color: #150000;
    font-weight: 500;
    white-space: nowrap;
  }
  .arrow {
    margin-left: 8px;
    font-size: 28px;
    color: #999;
  }
}
.bottom-app-bar,
.top-app-bar {
  position: fixed;
  left: 0;
  right: 0;
  z-index: 98;
  padding: 16px 20px;
  background: #FFF0ED;
  display: flex;
  align-items: center;
  .logo {
    width: 90px;
    height: 90px;
    min-width: 90px;
    border-radius: 8px;
    box-shadow: 0 0 6px #cacaca;
  }
  .tips {
    flex: 1;
    padding: 0 16px;
    overflow: hidden;
    .title {
      font-size: 24px;
      font-weight: 400;
      color: #150000;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .title-desc {
      margin-top: 6px;
      font-size: 22px;
      color: #666;
      font-weight: 400;
      line-height: 1.3;
    }
  }
  .open-btn {
    display: inline-block;
    width: 106px;
    height: 60px;
    line-height: 60px;
    text-align: center;
    color: #fff;
    border-radius: 8px;
    font-size: 26px;
    font-weight: 600;
    background-color: #E14646;
    white-space: nowrap;
    text-decoration: none;
  }
  .close-icon {
    margin-left: 16px;
    color: #999;
    font-size: 28px;
    cursor: pointer;
    flex-shrink: 0;
  }
}
.bottom-app-bar {
  bottom: 0;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
}
.top-app-bar {
  top: 0;
  z-index: 101;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.search-bar-wrapper{
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  box-shadow: 0 0 4px #cacaca;
  &.has-top-app-bar {
    top: $top-app-bar-offset;
  }
}
:global(html[data-kili-app-entry="browser"] .listing-detail-header:not(.app-entry-disabled)) {
  top: $top-app-bar-offset;
}
:global(html[data-kili-app-entry="native"] .listing-detail-header) {
  top: 0;
}
.search-bar-content{
  padding-top: 20px;
  background-color: #fff;
}
.search-bar{
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
  padding-right: 20px;
  .back{
    padding-left: 20px;
    width: 80px;
    text-align: left;
    i{ font-size: 50px; }
  }
  .search-input{
    height: 100%;
    padding: 0 20px;
    flex: 1;
    display: flex;
    align-items: center;
    background-color: #f5f5f5;
    border-radius: 60px;
    i{ font-size: 50px; }
    .search-text{
      margin-left: 10px;
      font-size: 24px;
      color: #999;
    }
  }
  .quick-btn{
    margin-left: 20px;
    width: 70px;
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    overflow: hidden;
    border-radius: 50%;
    i { font-size: 38px; color: black;}
  }
}
.mark-bar{
  display: flex;
  align-content: center;
  font-size: 24px;
  .mark{
    flex: 1;
    text-align: center;
    .mark-label{
      height: 80px;
      transition: all 0.3s;
      line-height: 80px;
      display: inline-block;
      border-bottom: 8px solid transparent;
    }
    .mark-label[select="1"] {
      border-color: var(--default_color);
      color: var(--default_color);
      text-shadow: 0 0 1px var(--default_color);
    }
  }
}
.share-title{
  color: #333;
  font-weight: 600;
  font-size: 36px;
  text-align: center;
  padding: 30px 0;
}
.share-product-card{
  display: flex;
  align-items: center;
  background-color: #f2f2f2;
  padding: 20px;
  margin: 0 30px;
  border-radius: 10px;
  overflow: hidden;
  .product-image{
    width: 120px;
    border-radius: 10px;
    min-width: 120px;
    height: 120px;
    margin-right: 20px;
    object-fit: contain;
    background-color: #fff;
  }
  .product-name{
    flex: 1;
    @include ellipsisMultiline(2);
    font-size: 26px;
    line-height: 40px;
    color: #333;
  }
}
</style>
